import * as React from 'react';
import '../css/key-art-carousel.sass';
import leftArrow from '../css/assets/img/carousel-left-arrow.svg';
import rightArrow from '../css/assets/img/carousel-right-arrow.svg';

const Carousel = require('react-multi-carousel');

export interface ResponsiveType {
  desktop: {
    min: number;
    max: number;
    type: 'desktop' | 'lg';
    items?: number;
    slidesToSlide?: number;
    partialVisibilityGutter?: number;
  };
  tablet: {
    min: number;
    max: number;
    type: 'tablet' | 'md';
    items?: number;
    slidesToSlide?: number;
    partialVisibilityGutter?: number;
  };
  mobile: {
    min: number;
    max: number;
    type: 'mobile' | 'sm';
    items?: number;
    slidesToSlide?: number;
    partialVisibilityGutter?: number;
  };
}
interface KeyArtCarouselProps {
  children: any;
  onChange?: Function;
  responsive?: ResponsiveType;
  infinite?: boolean;
  centerMode?: boolean;
  autoPlaySpeed?: string;
  id: string;
}

const getDeviceType = (responsive: ResponsiveType) => {
  if (
    window.innerWidth <= responsive.mobile.max &&
    window.innerWidth >= responsive.mobile.min
  )
    return responsive.mobile.type;
  if (
    window.innerWidth <= responsive.tablet.max &&
    window.innerWidth >= responsive.tablet.min
  )
    return responsive.tablet.type;
  return responsive.desktop.type;
};

function CustomArrow(arrowProps: any) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { carouselState, children, ...restArrowProps } = arrowProps;
  return <span {...restArrowProps}> {children} </span>;
}

export default function KeyArtPosterCarousel(props: KeyArtCarouselProps) {
  const {
    responsive = {
      desktop: {
        min: 993,
        max: 4000,
        type: 'desktop',
      },
      tablet: {
        min: 768,
        max: 992,
        type: 'tablet',
      },
      mobile: {
        min: 0,
        max: 767,
        type: 'mobile',
      },
    },
  } = props;
  const carouselSettings = {
    [responsive.desktop.type]: {
      breakpoint: {
        max: responsive.desktop.max,
        min: responsive.desktop.min,
      },
      items: responsive.desktop.items || 6,
      slidesToSlide: responsive.desktop.slidesToSlide || 4,
      partialVisibilityGutter: responsive.desktop.partialVisibilityGutter || 10,
    },
    [responsive.tablet.type]: {
      breakpoint: {
        max: responsive.tablet.max,
        min: responsive.tablet.min,
      },
      items: responsive.tablet.items || 4,
      slidesToSlide: responsive.tablet.slidesToSlide || 2,
      partialVisibilityGutter: responsive.tablet.partialVisibilityGutter || 10,
    },
    [responsive.mobile.type]: {
      breakpoint: {
        max: responsive.mobile.max,
        min: responsive.mobile.min,
      },
      items: responsive.mobile.items || 2,
      slidesToSlide: responsive.mobile.slidesToSlide || 1,
      partialVisibilityGutter: responsive.mobile.partialVisibilityGutter || 10,
    },
  };
  const children = React.Children.toArray(props.children).filter(
    (child: any) => {
      return React.isValidElement(child) && child.type !== React.Fragment;
    }
  );

  const ref: any = React.useRef();
  const carouselRef: any = React.useRef();

  const cycleTime = parseInt(props.autoPlaySpeed || '0', 10);

  const removeAriaHidden = () => {
    const carouselList = ref.current.getElementsByClassName(
      'react-multi-carousel-track'
    );
    if (carouselList.length) {
      const { childNodes } = carouselList[0];
      childNodes.forEach((elm: any) => {
        elm.removeAttribute('aria-hidden');
      });
    }
  };

  React.useEffect(() => {
    if (ref && ref.current) {
      removeAriaHidden();
    }
  }, [ref]);

  const handleTab = (e: any) => {
    if (e.keyCode === 9) {
      const carouselItemIndex = parseInt(
        e.target.parentNode.parentNode.getAttribute('data-index'),
        10
      );
      const carouselContainer: any = carouselRef.current;
      if (e.shiftKey) {
        if (carouselItemIndex <= carouselContainer.state.currentSlide + 1) {
          carouselContainer.previous();
        }
      } else if (carouselItemIndex > carouselContainer.state.currentSlide + 3) {
        carouselContainer.next();
      }
    }
  };

  React.useEffect(() => {
    document.addEventListener('keydown', handleTab);
    return () => {
      document.removeEventListener('keydown', handleTab);
    };
  }, []);

  return (
    <div id={props.id} ref={ref}>
      {/* eslint-disable-next-line react/jsx-pascal-case */}
      <Carousel.default
        ref={carouselRef}
        responsive={carouselSettings}
        removeArrowOnDeviceType={[
          responsive.tablet.type,
          responsive.mobile.type,
        ]}
        centerMode={
          props.centerMode &&
          !(
            getDeviceType(responsive) === 'mobile' ||
            getDeviceType(responsive) === 'sm'
          )
        }
        dir="ltr"
        rtl=""
        infinite={props.infinite}
        autoPlay={cycleTime > 0}
        autoPlaySpeed={cycleTime > 0 && cycleTime}
        afterChange={() => {
          if (props.onChange) {
            props.onChange();
          }
          const firstActiveSlide = ref.current?.getElementsByClassName(
            'react-multi-carousel-item--active'
          )[0];
          const focusableTitle: any =
            firstActiveSlide?.getElementsByClassName('title')[0];
          if (focusableTitle) {
            removeAriaHidden();
            focusableTitle.focus();
          }
        }}
        partialVisible={
          !props.centerMode ||
          getDeviceType(responsive) === 'mobile' ||
          getDeviceType(responsive) === 'sm'
        }
        ssr
        deviceType={getDeviceType(responsive)}
        customRightArrow={
          <CustomArrow>
            <img
              src={rightArrow}
              className="react-multiple-carousel__arrow right"
              alt="Next Slides"
            />
          </CustomArrow>
        }
        customLeftArrow={
          <CustomArrow>
            <img
              src={leftArrow}
              className="react-multiple-carousel__arrow left"
              alt="Previous Slides"
            />
          </CustomArrow>
        }
      >
        {children}
      </Carousel.default>
    </div>
  );
}
